import React, { useState, useCallback, useMemo } from 'react';
import { Space, Button, Input, Select, Tag, Grid } from 'antd';
import {
  DownloadOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import ExcelGuideModal from '../../SlotsPage/components/ExcelGuideModal';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { isSlotOperationRestricted } from '../../../Utils/timeRestriction';
import { getBatchPermissions } from '../../../Utils/slotPermissions';
import { Slot } from '../../../Modules/Slot/Model/Slot';
import styled from 'styled-components';

const { Option } = Select;

interface Props {
  selected: React.Key[];
  selectedSlots: Slot[];
  searchField: 'slot' | 'keyword' | 'url' | 'user' | 'mid_single' | 'mid_compare';
  onSearchFieldChange: (v: Props['searchField']) => void;
  onDelete: () => void;
  search: string;
  onSearchChange: (v: string) => void;
  onSearch: (
    value: string,
    event?: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLInputElement>,
    info?: { source?: 'clear' | 'input' },
  ) => void;
  status: string;
  onStatusChange: (v: string) => void;
  onExcelDownload: () => void;
  excelUploading: boolean;
  excelDownloading?: boolean;
}

const Bar = styled.div`
  width: 100%;
  padding: 12px 16px;
  border-radius: 0;
  background: transparent;
  border: none;
  display: grid;
  grid-template-rows: auto auto;
  gap: 10px;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
`;

const RightCluster = styled(Space)`
  display: flex;
  flex-shrink: 0;
`;

const SearchArea = styled(Space)`
  flex: 1;
  min-width: 280px;
`;

const SelectedTag = styled(Tag)`
  && {
    background: rgba(176, 48, 82, 0.08);
    border-color: rgba(176, 48, 82, 0.2);
    color: #B03052;
    margin-right: 4px;
  }
`;

const ManagementBar: React.FC<Props> = ({
  selected,
  selectedSlots,
  searchField,
  onSearchFieldChange,
  onDelete,
  search,
  onSearchChange,
  onSearch,
  status,
  onStatusChange,
  onExcelDownload,
  excelUploading,
  excelDownloading = false,
}) => {
  const hasSelection = selected.length > 0;
  const [guideOpen, setGuideOpen] = useState(false);
  const currentUser = useAppSelector(selectUser);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  
  // 시간 제한 확인
  const isRestricted = currentUser ? isSlotOperationRestricted(currentUser) : false;

  // 선택 슬롯 권한 계산은 선택/사용자 변경 때만 재계산
  const batchPermissions = useMemo(
    () => getBatchPermissions(selectedSlots, currentUser, isRestricted),
    [selectedSlots, currentUser, isRestricted]
  );

  const handleSearchPressEnter = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.nativeEvent.isComposing) {
        return;
      }
      onSearch(e.currentTarget.value, e, { source: 'input' });
    },
    [onSearch],
  );

  return (
    <Bar>
      <Row>
        <SearchArea size={isMobile ? 'small' : 'middle'} wrap style={{ width: isMobile ? '100%' : undefined }}>
          <Select size={isMobile ? 'small' : 'middle'} value={searchField} onChange={onSearchFieldChange} style={{ width: 140 }}>
            <Option value="slot">슬롯 번호</Option>
            <Option value="user">아이디</Option>
            <Option value="keyword">키워드</Option>
            <Option value="url">URL</Option>
            <Option value="mid_single">단일 MID</Option>
            <Option value="mid_compare">가격비교 MID</Option>
          </Select>
          {searchField !== 'mid_single' && searchField !== 'mid_compare' && (
            <Input.Search
              size={isMobile ? 'small' : 'middle'}
              placeholder={searchField === 'url' ? 'URL 입력' : '검색어 입력'}
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              onSearch={onSearch}
              onPressEnter={handleSearchPressEnter}
              allowClear
              enterButton={<span><SearchOutlined /> 검색</span>}
              style={{ width: '100%' }}
            />
          )}
          {(searchField === 'mid_single' || searchField === 'mid_compare') && (
            <Input.Search
              size={isMobile ? 'small' : 'middle'}
              placeholder={`${searchField === 'mid_single' ? '단일' : '가격비교'} MID 입력`}
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              onSearch={onSearch}
              onPressEnter={handleSearchPressEnter}
              allowClear
              enterButton={<span><SearchOutlined /> 검색</span>}
              style={{ width: '100%' }}
            />
          )}
        </SearchArea>
 
        <RightCluster size={isMobile ? 'small' : 'middle'} wrap style={{ width: isMobile ? '100%' : undefined }}>
          {selected.length > 0 && (
            <SelectedTag>선택 {selected.length}개</SelectedTag>
          )}
          {/* 삭제만 가능 */}
          {currentUser?.grade === "ADMIN" && (
            <Button size={isMobile ? 'small' : 'middle'} danger icon={<DeleteOutlined />} onClick={onDelete} disabled={!hasSelection || !batchPermissions.canDelete}>삭제</Button>
          )}
          <Button size={isMobile ? 'small' : 'middle'} icon={<DownloadOutlined />} onClick={onExcelDownload} loading={excelDownloading}>
            엑셀 다운로드{selected.length > 0 && ` (${selected.length}개)`}
          </Button>
          <Button size={isMobile ? 'small' : 'middle'} icon={<FileTextOutlined />} onClick={() => setGuideOpen(true)}>엑셀 가이드</Button>
        </RightCluster>
      </Row>

      <ExcelGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </Bar>
  );
};

export default ManagementBar; 